import {
  getPopulationUserIds,
  IActiveLearnerSearchPayload,
} from 'components/admin/dashboard/api/search';
import { useInfiniteSearchActiveTeamMembers } from 'components/admin/dashboard/hooks';
import { InfiniteScrollSpinner, NoResults } from 'components/admin/ui';
import { Users01 } from 'ethena-icons';
import { useDebounce } from 'lib/hooks';
import { IFetchResponse } from 'lib/requests/fetch';
import _ from 'lodash';
import { ReactNode, useEffect, useState } from 'react';
import {
  SelectPopulationModal,
  useSelectPopulationModal,
} from '../SelectPopulationModal';
import { PopulationTable } from './PopulationTable';

interface IAddPopulationModalProps {
  excludeUserIds: number[];
  getUserIds?: (payload: {
    excludeUserIds: number[];
  }) => Promise<IFetchResponse<number[]>>;
  isVisible: boolean;
  onClose: () => void;
  onSubmit: (teamMemberIds: number[]) => void;
  populationName?: string;
  selectAllTooltipLabel?: string;
  title?: ReactNode;
  useSearch?: typeof useInfiniteSearchActiveTeamMembers;
}

export const AddPopulationModal = ({
  excludeUserIds,
  getUserIds = getPopulationUserIds,
  isVisible,
  onClose,
  onSubmit,
  populationName = 'Team Members',
  selectAllTooltipLabel,
  title,
  useSearch = useInfiniteSearchActiveTeamMembers,
}: IAddPopulationModalProps) => {
  const [populationCount, setPopulationCount] = useState<number>(0);

  const context = useSelectPopulationModal({
    onClose,
    selectAllPopulationCount: populationCount,
    populationName,
  });
  const {
    deselectedPreviewUserIds,
    getIsSelected,
    handleClose,
    handleTableRowClick,
    isPreview,
    isSelectAll,
    selectedUserIds,
    searchTerm,
    selectedCustomCategoryValue,
    selectedDepartment,
    selectedEmployeeType,
    selectedLocations,
    selectedRole,
  } = context;

  const debouncedSearchTerm = useDebounce<string>(searchTerm, 250);

  const getSearchPayload = (): Omit<
    IActiveLearnerSearchPayload,
    'limit' | 'offset'
  > => {
    if (isPreview) {
      if (isSelectAll) {
        return {
          userIdsFilter: {
            exclude: true,
            userIds: _.concat(selectedUserIds, excludeUserIds),
          },
        };
      } else {
        return { userIdsFilter: { exclude: false, userIds: selectedUserIds } };
      }
    } else {
      return {
        userIdsFilter: {
          exclude: true,
          userIds: excludeUserIds,
        },
        userSearchTerm: debouncedSearchTerm,
        attributes: {
          customCategoryValues: _.isUndefined(selectedCustomCategoryValue)
            ? undefined
            : [selectedCustomCategoryValue],
          departments: _.isUndefined(selectedDepartment)
            ? undefined
            : [selectedDepartment],
          employeeTypes: _.isUndefined(selectedEmployeeType)
            ? undefined
            : [selectedEmployeeType],
          locations: _.isEmpty(selectedLocations.locations)
            ? undefined
            : selectedLocations.locations,
          roles: _.isUndefined(selectedRole) ? undefined : [selectedRole],
        },
      };
    }
  };

  const {
    data: teamMembersSearchResult,
    isValidating,
    scrollRef,
  } = useSearch(getSearchPayload(), {
    shouldFetch: isVisible,
  });

  useEffect(() => {
    const searchCount = _.first(teamMembersSearchResult)?.total;
    // Guaranteed to load the unfiltered total population count first. Don't
    // want that value to get overwritten by a filtered count.
    setPopulationCount(
      searchCount > populationCount ? searchCount : populationCount
    );
  }, [teamMembersSearchResult]);

  const teamMembers = _.chain(teamMembersSearchResult)
    .flatMap((x) => x.results)
    .reject((x) => _.includes(deselectedPreviewUserIds, x.userId))
    .value();

  const handleSubmit = async () => {
    if (isSelectAll) {
      const { data } = await getUserIds({
        excludeUserIds: _.concat(
          selectedUserIds,
          excludeUserIds,
          deselectedPreviewUserIds
        ),
      });
      onSubmit(data);
    } else {
      onSubmit(_.difference(selectedUserIds, deselectedPreviewUserIds));
    }
    handleClose();
  };

  return (
    <SelectPopulationModal
      handleSubmit={handleSubmit}
      isVisible={isVisible}
      title={title}
      {...context}
    >
      <SelectPopulationModal.Body>
        <PopulationTable
          getIsSelected={getIsSelected}
          isBorderless
          onClick={handleTableRowClick}
          teamMembers={teamMembers}
        />

        {_.isEmpty(teamMembers) && !isValidating && (
          <NoResults
            icon={Users01}
            title={`No ${populationName} Found`}
            message="Adjust your search or filters"
          />
        )}
        <InfiniteScrollSpinner ref={scrollRef} isValidating={isValidating} />
      </SelectPopulationModal.Body>

      <SelectPopulationModal.Footer
        selectAllTooltipLabel={selectAllTooltipLabel}
      />
    </SelectPopulationModal>
  );
};
